import { useState, useEffect, lazy, Suspense } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import Sidebar from './components/Sidebar'
import RequireAuth from './components/RequireAuth'
import AccessDenied from './components/AccessDenied'
import { PermissionsProvider, usePermissions } from './contexts/PermissionsContext'

// Lazy-loaded route components for code splitting
const Overview = lazy(() => import('./pages/Overview'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const DeviceDetail = lazy(() => import('./pages/DeviceDetail'))
const Events = lazy(() => import('./pages/Events'))
const Computers = lazy(() => import('./pages/Computers'))
const Trendlines = lazy(() => import('./pages/Trendlines'))
const TCO = lazy(() => import('./pages/TCO'))
const Insights = lazy(() => import('./pages/Insights'))
const Integrations = lazy(() => import('./pages/Integrations'))
const Api = lazy(() => import('./pages/Api'))
const Settings = lazy(() => import('./pages/Settings'))

// Settings sub-pages
const CurrencySettings = lazy(() => import('./pages/settings-pages/CurrencySettings'))
const HardwareSettings = lazy(() => import('./pages/settings-pages/HardwareSettings'))
const HealthThresholdSettings = lazy(() => import('./pages/settings-pages/HealthThresholdSettings'))
const UsersGroupsSettings = lazy(() => import('./pages/settings-pages/UsersGroupsSettings'))
const UsersSettings = lazy(() => import('./pages/settings-pages/UsersSettings'))
const APIKeysSettings = lazy(() => import('./pages/settings-pages/APIKeysSettings'))
const StalenessSettings = lazy(() => import('./pages/settings-pages/StalenessSettings'))
const SystemLogs = lazy(() => import('./pages/settings-pages/SystemLogs'))
const TimezoneSettings = lazy(() => import('./pages/settings-pages/TimezoneSettings'))
const ServerMaintenance = lazy(() => import('./pages/settings-pages/ServerMaintenance'))
const ApplicationIntelligence = lazy(() => import('./pages/settings-pages/ApplicationIntelligence'))
const AuditSettings = lazy(() => import('./pages/settings-pages/AuditSettings'))
const BasicAuthUsersSettings = lazy(() => import('./pages/settings-pages/BasicAuthUsersSettings'))

// Integration sub-pages
const JamfProIntegration = lazy(() => import('./pages/integrations/JamfProIntegration'))
const JamfRoutinesIntegration = lazy(() => import('./pages/integrations/JamfRoutinesIntegration'))
const ABMIntegration = lazy(() => import('./pages/integrations/ABMIntegration'))
const WebhooksIntegration = lazy(() => import('./pages/integrations/WebhooksIntegration'))
const IntegrationLogs = lazy(() => import('./pages/integrations/IntegrationLogs'))

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
)

const RouteLoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '200px' }}>
    <div className="loading-spinner" />
  </div>
)

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const { accessDenied, isLoading } = usePermissions()

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  // Close sidebar on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false)
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [])

  // Show loading state while checking permissions
  if (isLoading) {
    return (
      <div className="access-denied-container">
        <div className="access-denied-card">
          <div className="loading-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  // Show access denied if user is not authorized
  if (accessDenied) {
    return <AccessDenied />
  }

  return (
    <div className="app-layout">
        <button
          className="mobile-menu-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label="Toggle menu"
        >
          <MenuIcon />
        </button>
        <div
          className={`sidebar-overlay ${sidebarOpen ? 'visible' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />
        <Sidebar isOpen={sidebarOpen} />
        <main className="main-content">
          <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
              <Route path="/" element={<Overview />} />
              <Route path="/health" element={<Dashboard />} />
              <Route path="/health/refresh" element={<Dashboard />} />
              <Route path="/health/alerts" element={<Dashboard />} />
              <Route path="/health/performance" element={<Dashboard />} />
              <Route path="/device/:uuid" element={<DeviceDetail />} />
              <Route path="/events" element={<Events />} />
              <Route path="/computers" element={<Computers />} />
              <Route path="/trendlines" element={<Trendlines />} />
              <Route path="/tco" element={<TCO />} />
              <Route path="/tco/app-intelligence" element={<TCO />} />
              <Route path="/tco/app-intelligence/recovery" element={<TCO />} />
              <Route path="/tco/app-intelligence/compliance" element={<TCO />} />
              <Route path="/tco/app-intelligence/analytics" element={<TCO />} />
              <Route path="/tco/refresh" element={<TCO />} />
              <Route path="/tco/hardware-fit" element={<TCO />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/integrations" element={<Integrations />} />
              <Route path="/integrations/jamf-pro" element={<JamfProIntegration />} />
              <Route path="/integrations/jamf-routines" element={<JamfRoutinesIntegration />} />
              <Route path="/integrations/abm" element={<ABMIntegration />} />
              <Route path="/integrations/webhooks" element={<WebhooksIntegration />} />
              <Route path="/integrations/logs" element={<IntegrationLogs />} />
              <Route path="/api" element={<Api />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/settings/currency" element={<CurrencySettings />} />
              <Route path="/settings/hardware" element={<HardwareSettings />} />
              <Route path="/settings/health" element={<HealthThresholdSettings />} />
              <Route path="/settings/staleness" element={<StalenessSettings />} />
              <Route path="/settings/users-groups" element={<UsersGroupsSettings />} />
              <Route path="/settings/users" element={<UsersSettings />} />
              <Route path="/settings/api-keys" element={<APIKeysSettings />} />
              <Route path="/settings/logs" element={<SystemLogs />} />
              <Route path="/settings/timezone" element={<TimezoneSettings />} />
              <Route path="/settings/maintenance" element={<ServerMaintenance />} />
              <Route path="/settings/app-intelligence" element={<ApplicationIntelligence />} />
              <Route path="/settings/audit" element={<AuditSettings />} />
              <Route path="/settings/basic-users" element={<BasicAuthUsersSettings />} />
            </Routes>
          </Suspense>
        </main>
      </div>
  )
}

function App() {
  return (
    <RequireAuth>
      <PermissionsProvider>
        <AppContent />
      </PermissionsProvider>
    </RequireAuth>
  )
}

export default App
